import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, Button, Form, Input, Row, Col, Spin, message } from "antd";
import { ArrowLeftOutlined, SaveOutlined } from "@ant-design/icons";
import apiClient from '../../services/apiClient';
import "./AdminPages.css";

/**
 * Full-page editor for a single termsheet record.
 * Opened from TermsheetAdmin via /admin/manage-termsheets/edit/:isin
 */
const TermsheetEditAdmin = () => {
  const { isin } = useParams();
  const navigate = useNavigate();

  const baseUrl = process.env.REACT_APP_BASE_URL || "http://localhost:8080";
  const token = localStorage.getItem("token");

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [termsheet, setTermsheet] = useState(null);

  const authHeaders = useMemo(() => {
    const headers = { "Content-Type": "application/json" };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    return headers;
  }, [token]);

  useEffect(() => {
    if (!isin) return;
    const fetchTermsheet = async () => {
      setLoading(true);
      try {
        const response = await apiClient.get(
          `${baseUrl}/termsheet/${encodeURIComponent(isin)}`,
          { headers: authHeaders }
        );
        if (response.status === 404 || response.status === 204) {
          message.error("No termsheet found for this ISIN");
          setTermsheet(null);
          return;
        }
        const result = response.data;
        const data = result.data || null;
        setTermsheet(data);
        if (data) {
          form.setFieldsValue(data);
        }
      } catch (err) {
        if (err.response?.status === 404 || err.response?.status === 204) {
          message.error("No termsheet found for this ISIN");
          setTermsheet(null);
        } else {
          console.error("Error fetching termsheet:", err);
          message.error(err.message || "Failed to load termsheet");
          setTermsheet(null);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchTermsheet();
  }, [isin, baseUrl, authHeaders, form]);

  const getLabelFromField = (field) => {
    if (!field) return "";
    const withSpaces = field
      .replace(/_/g, " ")
      .replace(/([A-Z])/g, " $1")
      .toLowerCase();
    return withSpaces
      .split(" ")
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  };

  const handleSave = async () => {
    if (!isin) {
      message.warning("Missing ISIN in route");
      return;
    }
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        ...(termsheet || {}),
        ...values,
        isin,
      };

      const response = await apiClient.put(
        `${baseUrl}/termsheet/${encodeURIComponent(isin)}`,
        payload,
        {
          headers: authHeaders,
        }
      );
      const result = response.data;
      setTermsheet(result.data || payload);
      message.success("Termsheet updated successfully");
      navigate(-1);
    } catch (err) {
      if (err?.errorFields) return;
      console.error("Error updating termsheet:", err);
      const errMsg =
        err.response?.data?.message ||
        err.response?.data ||
        err.message;
      message.error(errMsg || "Failed to update termsheet");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-header-banner">
        <div className="hero-content">
          <h1 className="hero-title-news">Edit Termsheet</h1>
          <p style={{ marginTop: 8 }}>
            ISIN: <strong>{isin}</strong>
          </p>
        </div>
      </div>

      <Card className="admin-main-card">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 16,
          }}
        >
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
            Back
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={saving}
          >
            Save Changes
          </Button>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "2rem" }}>
            <Spin size="large" />
          </div>
        ) : !termsheet ? (
          <div style={{ textAlign: "center", padding: "2rem" }}>
            <p>No termsheet data found to edit.</p>
          </div>
        ) : (
          <Form layout="vertical" form={form}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="isin" label="ISIN">
                  <Input disabled />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              {Object.keys(termsheet)
                .filter(
                  (field) =>
                    field !== "isin" &&
                    field !== "createdAt" &&
                    field !== "updatedAt" &&
                    field !== "masterISIN"
                )
                .map((field) => (
                  <Col span={12} key={field}>
                    <Form.Item name={field} label={getLabelFromField(field)}>
                      <Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} />
                    </Form.Item>
                  </Col>
                ))}
            </Row>
          </Form>
        )}
      </Card>
    </div>
  );
};

export default TermsheetEditAdmin;


